import { api } from '@/config/api';

// ── types ────────────────────────────────────────────────────────────────────

export interface ApptSlotWindow { start: string; end: string; capacity?: number | null }
export interface ApptDayHours { weekday: number; isOpen: boolean; windows: ApptSlotWindow[] }
export interface ApptClosure {
  closureUid: string;
  scope: 'COMPANY' | 'SERVICE';
  dateFrom: string;
  dateTo: string;
  reason?: string | null;
}

export interface ApptService {
  serviceUid: string;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  durationMinutes: number;
  price?: number | null;
  currency?: string | null;
  bufferBeforeMin: number;
  bufferAfterMin: number;
  maxAdvanceDays: number;
  minLeadMinutes: number;
  capacityPerSlot: number;
  timezone?: string | null;
  isActive: boolean;
  bookingCount?: number;
  createdAt?: string | null;
  hours?: ApptDayHours[];
  closures?: ApptClosure[];
}

export interface ApptSlot { start: string; end: string; available: boolean; remaining: number }

export type ApptBookingStatus = 'PENDING' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
export type ApptBookingSource = 'ADMIN' | 'WEB' | 'KIOSK' | 'AI_AGENT' | 'WHATSAPP';

export interface ApptBooking {
  bookingUid: string;
  bookingNo: string;
  serviceUid?: string | null;
  serviceName?: string | null;
  durationMinutes?: number;
  contactUid?: string | null;
  customerName?: string | null;
  customerPhone?: string | null;
  customerEmail?: string | null;
  phoneVerified?: boolean;
  bookingDate: string;
  slotStart: string;
  slotEnd: string;
  timezone?: string | null;
  status: ApptBookingStatus;
  source: ApptBookingSource;
  notes?: string | null;
  cancelReason?: string | null;
  cancelledByName?: string | null;
  createdAt?: string | null;
}

export interface ApptCalendarBooking {
  bookingUid: string;
  bookingNo: string;
  customerName?: string | null;
  customerPhone?: string | null;
  customerEmail?: string | null;
  notes?: string | null;
  status: ApptBookingStatus;
  source: string;
  date: string;
  start: string;
  end: string;
  serviceName: string;
  serviceUid: string;
}

export interface UpsertApptServiceInput {
  name: string;
  description?: string;
  imageUrl?: string;
  durationMinutes?: number;
  price?: number | null;
  currency?: string;
  bufferBeforeMin?: number;
  bufferAfterMin?: number;
  maxAdvanceDays?: number;
  minLeadMinutes?: number;
  capacityPerSlot?: number;
  isActive?: boolean;
  hours?: ApptDayHours[];
}

const BASE = '/slighter_crm/appointments';

// ── services ─────────────────────────────────────────────────────────────────

export const appointmentsService = {
  listServices: async (opts?: { keyword?: string; includeInactive?: boolean }): Promise<{ items: ApptService[] }> =>
    (await api.get(`${BASE}/services`, {
      params: {
        keyword: opts?.keyword || undefined,
        includeInactive: String(opts?.includeInactive ?? true),
      },
    })).data,

  getService: async (serviceUid: string): Promise<{ item: ApptService }> =>
    (await api.get(`${BASE}/services/${serviceUid}`)).data,

  createService: async (input: UpsertApptServiceInput): Promise<{ serviceUid: string }> =>
    (await api.post(`${BASE}/services`, input)).data,

  updateService: async (serviceUid: string, input: UpsertApptServiceInput): Promise<{ serviceUid: string }> =>
    (await api.patch(`${BASE}/services/${serviceUid}`, input)).data,

  deleteService: async (serviceUid: string): Promise<void> =>
    (await api.delete(`${BASE}/services/${serviceUid}`)).data,

  closureAdd: async (input: { serviceUid?: string; dateFrom: string; dateTo: string; reason?: string }):
    Promise<{ closureUid: string }> => (await api.post(`${BASE}/closures`, input)).data,

  closureDelete: async (closureUid: string): Promise<void> =>
    (await api.delete(`${BASE}/closures/${closureUid}`)).data,

  /** The company's public booking-link identity (slug + name). */
  bookingLink: async (): Promise<{ slug: string; name: string }> =>
    (await api.get(`${BASE}/booking-link`)).data,

  // ── availability ──
  /** `reason` says why a day has no bookable time (closed, a day off, fully booked…) — null when there is one. */
  availableSlots: async (serviceUid: string, date: string): Promise<{ date: string; slots: ApptSlot[]; reason?: string | null }> =>
    (await api.get(`${BASE}/slots`, { params: { serviceUid, date } })).data,

  /** The next days (from `from`, default today) on which the service has ≥1 bookable slot. */
  availableDates: async (serviceUid: string, limit?: number, from?: string): Promise<{ dates: Array<{ date: string; weekday: string; slotCount: number }> }> =>
    (await api.get(`${BASE}/available-dates`, { params: { serviceUid, limit, from } })).data,

  // ── bookings ──
  listBookings: async (f?: {
    dateFrom?: string; dateTo?: string; status?: string; serviceUid?: string; keyword?: string;
    statuses?: string[]; serviceUids?: string[];
  }): Promise<{ items: ApptBooking[] }> =>
    (await api.get(`${BASE}/bookings`, {
      params: {
        dateFrom: f?.dateFrom, dateTo: f?.dateTo, status: f?.status,
        serviceUid: f?.serviceUid, keyword: f?.keyword,
        statuses: f?.statuses?.length ? f.statuses.join(',') : undefined,
        serviceUids: f?.serviceUids?.length ? f.serviceUids.join(',') : undefined,
      },
    })).data,

  getBooking: async (bookingUid: string): Promise<{ item: ApptBooking }> =>
    (await api.get(`${BASE}/bookings/${bookingUid}`)).data,

  createBooking: async (input: {
    serviceUid: string; bookingDate: string; slotStart: string; slotEnd: string;
    customerName?: string; customerPhone?: string; customerEmail?: string;
    notes?: string; autoConfirm?: boolean; source?: string;
  }): Promise<{ bookingUid: string; bookingNo: string; status: ApptBookingStatus }> =>
    (await api.post(`${BASE}/bookings`, input)).data,

  updateBooking: async (bookingUid: string, input: {
    bookingDate?: string; slotStart?: string; slotEnd?: string; notes?: string;
    customerName?: string; customerPhone?: string; customerEmail?: string;
  }): Promise<{ bookingUid: string }> =>
    (await api.patch(`${BASE}/bookings/${bookingUid}`, input)).data,

  setBookingStatus: async (bookingUid: string, status: 'CONFIRMED' | 'COMPLETED' | 'NO_SHOW'): Promise<{ status: string }> =>
    (await api.post(`${BASE}/bookings/${bookingUid}/status`, { status })).data,

  cancelBooking: async (bookingUid: string, reason: string): Promise<void> =>
    (await api.post(`${BASE}/bookings/${bookingUid}/cancel`, { reason })).data,

  // ── calendar feed + search ──
  calendarFeed: async (dateFrom: string, dateTo: string): Promise<{ bookings: ApptCalendarBooking[] }> =>
    (await api.get(`${BASE}/calendar`, { params: { dateFrom, dateTo } })).data,

  calendarSearch: async (keyword: string, statuses?: string[], serviceUids?: string[]):
    Promise<{ bookings: ApptCalendarBooking[] }> =>
    (await api.get(`${BASE}/calendar-search`, {
      params: {
        keyword,
        statuses: statuses?.length ? statuses.join(',') : undefined,
        serviceUids: serviceUids?.length ? serviceUids.join(',') : undefined,
      },
    })).data,
};

// ── public (customer-view) booking flow — no auth ────────────────────────────

export const publicBookingService = {
  resolve: async (companySlug: string): Promise<{
    company: { name: string; logoUrl?: string | null; slug: string };
    services: ApptService[];
  }> => (await api.get(`/booking/${companySlug}`)).data,

  slots: async (companySlug: string, serviceUid: string, date: string): Promise<{ date: string; slots: ApptSlot[] }> =>
    (await api.get(`/booking/${companySlug}/slots`, { params: { serviceUid, date } })).data,

  availableDates: async (companySlug: string, serviceUid: string, limit?: number):
    Promise<{ dates: Array<{ date: string; weekday: string; slotCount: number }> }> =>
    (await api.get(`/booking/${companySlug}/available-dates`, { params: { serviceUid, limit } })).data,

  book: async (companySlug: string, input: {
    serviceUid: string; bookingDate: string; slotStart: string; slotEnd: string;
    customerName: string; customerPhone: string; customerEmail?: string; notes?: string;
  }): Promise<{ bookingUid: string; bookingNo: string; status: ApptBookingStatus }> =>
    (await api.post(`/booking/${companySlug}/book`, input)).data,

  myBookings: async (companySlug: string, phone: string): Promise<{ items: ApptBooking[] }> =>
    (await api.get(`/booking/${companySlug}/my-bookings`, { params: { phone } })).data,

  reschedule: async (companySlug: string, input: {
    phone: string; bookingUid: string; bookingDate: string; slotStart: string; slotEnd: string;
  }): Promise<{ bookingUid: string }> =>
    (await api.post(`/booking/${companySlug}/reschedule`, input)).data,

  cancel: async (companySlug: string, input: { phone: string; bookingUid: string; reason?: string }):
    Promise<{ bookingUid: string }> =>
    (await api.post(`/booking/${companySlug}/cancel`, input)).data,
};

// ── shared display helpers ───────────────────────────────────────────────────

// Colour semantics: amber = awaiting action, blue = confirmed/upcoming,
// green = done, red = cancelled, grey = no-show.
export const APPT_STATUS_META: Record<ApptBookingStatus, { label: string; className: string }> = {
  PENDING:   { label: 'Pending',   className: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
  CONFIRMED: { label: 'Confirmed', className: 'bg-blue-500/15 text-blue-600 dark:text-blue-400' },
  COMPLETED: { label: 'Completed', className: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  CANCELLED: { label: 'Cancelled', className: 'bg-red-500/15 text-red-600 dark:text-red-400' },
  NO_SHOW:   { label: 'No-show',   className: 'bg-surface-alt text-text-muted' },
};

export const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** 12-hour display for "HH:mm". */
export const to12h = (t?: string | null) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  if (Number.isNaN(h)) return t;
  const ap = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m ?? 0).padStart(2, '0')} ${ap}`;
};

export const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
